// src/quiz/gre-mock2/quantitativeData.js
// GRE Mock 2 — Quantitative Reasoning.
// Section 1: 12 questions (21 min). Section 2: 15 questions (26 min).
// Question format: see ../greQuestionTypes.js
import { QC_OPTIONS } from "../greQuestionTypes";

const QC_QUESTION = "Compare Quantity A and Quantity B, using additional information centered above the two quantities if such information is given.";
const [QC_A, QC_B, QC_EQUAL, QC_UNKNOWN] = QC_OPTIONS;

const ENROLLMENT_TABLE = {
  caption: "Enrollment at Westbrook College by Program, 2022 and 2024",
  headers: ["Program", "2022", "2024"],
  rows: [
    ["Engineering", "1,200", "1,500"],
    ["Business", "1,600", "1,440"],
    ["Sciences", "900", "1,080"],
    ["Humanities", "800", "680"],
    ["Total", "4,500", "4,700"],
  ],
};

// ================= SECTION 1 =================

export const section1Questions = [
  {
    id: 1,
    question: QC_QUESTION,
    given: "x > 0",
    quantityA: "(x + 1)²",
    quantityB: "x² + 1",
    options: QC_OPTIONS,
    answer: QC_A,
    explanation: "(x + 1)² = x² + 2x + 1, so Quantity A − Quantity B = 2x, which is positive because x > 0.",
  },
  {
    id: 2,
    question: QC_QUESTION,
    given: "n is an integer and 3 < n < 7.",
    quantityA: "n² − 5n",
    quantityB: "0",
    options: QC_OPTIONS,
    answer: QC_UNKNOWN,
    explanation: "n can be 4, 5 or 6, giving n² − 5n = −4, 0 or 6. Quantity A can be less than, equal to, or greater than 0.",
  },
  {
    id: 3,
    question: QC_QUESTION,
    quantityA: "The number of distinct prime factors of 210",
    quantityB: "The number of distinct prime factors of 360",
    options: QC_OPTIONS,
    answer: QC_A,
    explanation: "210 = 2 × 3 × 5 × 7 has 4 distinct prime factors; 360 = 2³ × 3² × 5 has 3.",
  },
  {
    id: 4,
    question: QC_QUESTION,
    given: "The angles of a triangle measure x°, 2x° and 3x°.",
    quantityA: "x",
    quantityB: "30",
    options: QC_OPTIONS,
    answer: QC_EQUAL,
    explanation: "x + 2x + 3x = 180, so 6x = 180 and x = 30.",
  },
  {
    id: 5,
    question: "If (2ˣ)(4ˣ⁺¹) = 2¹¹, what is the value of x?",
    options: ["2", "3", "4", "5", "6"],
    answer: "3",
    explanation: "4ˣ⁺¹ = 2²ˣ⁺², so the left side is 2ˣ⁺²ˣ⁺² = 2³ˣ⁺². Setting 3x + 2 = 11 gives x = 3.",
  },
  {
    id: 6,
    question: "A store raises the price of a jacket by 20 percent and later discounts the new price by 25 percent. The final price is what percent of the original price?",
    options: ["85%", "90%", "95%", "100%", "105%"],
    answer: "90%",
    explanation: "Final price = 1.20 × 0.75 = 0.90 of the original, i.e. 90%. Successive percent changes multiply; they do not add.",
  },
  {
    id: 7,
    type: "numeric",
    question: "The average (arithmetic mean) of five numbers is 18. When one of the numbers is removed, the average of the remaining four numbers is 16. What number was removed?",
    answer: 26,
    explanation: "Sum of five numbers = 5 × 18 = 90. Sum of the remaining four = 4 × 16 = 64. The removed number is 90 − 64 = 26.",
  },
  {
    id: 8,
    type: "multi",
    question: "Which of the following integers are divisors of 2³ × 3² × 5?",
    options: ["12", "27", "40", "45", "50", "72"],
    answer: ["12", "40", "45", "72"],
    explanation: "A divisor can use at most 2³, 3² and 5¹. 12 = 2²·3, 40 = 2³·5, 45 = 3²·5 and 72 = 2³·3² qualify. 27 = 3³ and 50 = 2·5² exceed the available powers.",
  },
  {
    id: 9,
    question: "A rectangle has a perimeter of 34 and a diagonal of length 13. What is the area of the rectangle?",
    options: ["48", "60", "72", "84", "120"],
    answer: "60",
    explanation: "l + w = 17 and l² + w² = 169. Since (l + w)² = l² + w² + 2lw, 289 = 169 + 2lw, so lw = 60. (The sides are 5 and 12.)",
  },
  {
    id: 10,
    table: ENROLLMENT_TABLE,
    question: "Which program had the greatest percent increase in enrollment from 2022 to 2024?",
    options: ["Engineering", "Business", "Sciences", "Humanities", "Engineering and Sciences increased by the same percent"],
    answer: "Engineering",
    explanation: "Engineering: 300/1,200 = 25%. Sciences: 180/900 = 20%. Business (−10%) and Humanities (−15%) decreased.",
  },
  {
    id: 11,
    type: "numeric",
    table: ENROLLMENT_TABLE,
    question: "In 2024, Sciences enrollment was what percent of total enrollment, rounded to the nearest whole percent? (Enter the number only.)",
    answer: 23,
    explanation: "1,080 / 4,700 ≈ 0.2298, which is 23% to the nearest whole percent.",
  },
  {
    id: 12,
    type: "multi",
    table: ENROLLMENT_TABLE,
    question: "For which of the following programs was the 2024 enrollment a smaller share of total enrollment than the 2022 enrollment was?",
    options: ["Engineering", "Business", "Sciences", "Humanities"],
    answer: ["Business", "Humanities"],
    explanation: "Shares 2022 → 2024: Engineering 26.7% → 31.9%, Business 35.6% → 30.6%, Sciences 20.0% → 23.0%, Humanities 17.8% → 14.5%. Only Business and Humanities fell.",
  },
];

// ================= SECTION 2 =================

export const section2Questions = [
  {
    id: 13,
    question: QC_QUESTION,
    given: "0 < x < 1",
    quantityA: "x³",
    quantityB: "√x",
    options: QC_OPTIONS,
    answer: QC_B,
    explanation: "For 0 < x < 1, higher powers are smaller and roots are larger: x³ < x < √x. (Try x = 1/4: x³ = 1/64, √x = 1/2.)",
  },
  {
    id: 14,
    question: QC_QUESTION,
    quantityA: "0.3 percent of 500",
    quantityB: "1.5",
    options: QC_OPTIONS,
    answer: QC_EQUAL,
    explanation: "0.3 percent = 0.003, and 0.003 × 500 = 1.5.",
  },
  {
    id: 15,
    question: QC_QUESTION,
    given: "a and b are positive integers, and a + b = 10.",
    quantityA: "ab",
    quantityB: "25",
    options: QC_OPTIONS,
    answer: QC_UNKNOWN,
    explanation: "The product is largest when a = b = 5, giving ab = 25 (equal). Any other pair, such as a = 4, b = 6, gives ab < 25. Since both cases are possible, the relationship cannot be determined.",
  },
  {
    id: 16,
    question: QC_QUESTION,
    given: "A circle has circumference 10π.",
    quantityA: "The area of the circle",
    quantityB: "The area of a square whose side length equals the diameter of the circle",
    options: QC_OPTIONS,
    answer: QC_B,
    explanation: "Radius = 5, so the circle's area is 25π ≈ 78.5. The square has side 10 and area 100. The circle fits inside that square, so its area is smaller.",
  },
  {
    id: 17,
    question: QC_QUESTION,
    given: "S = {2, 4, 6, 8, 10} and T = {12, 14, 16, 18, 20}",
    quantityA: "The standard deviation of S",
    quantityB: "The standard deviation of T",
    options: QC_OPTIONS,
    answer: QC_EQUAL,
    explanation: "T is S with 10 added to every element. Adding a constant shifts the data but does not change its spread, so the standard deviations are equal.",
  },
  {
    id: 18,
    question: "If x² − 9x + 20 = 0 and y² − 7y + 12 = 0, what is the greatest possible value of x − y?",
    options: ["−1", "0", "1", "2", "3"],
    answer: "2",
    explanation: "x² − 9x + 20 = (x − 4)(x − 5), so x = 4 or 5. y² − 7y + 12 = (y − 3)(y − 4), so y = 3 or 4. The maximum of x − y is 5 − 3 = 2.",
  },
  {
    id: 19,
    question: "A box contains 4 red marbles and 6 blue marbles. If two marbles are drawn at random without replacement, what is the probability that at least one of them is red?",
    options: ["1/3", "2/5", "8/15", "2/3", "4/5"],
    answer: "2/3",
    explanation: "P(no red) = (6/10)(5/9) = 1/3. So P(at least one red) = 1 − 1/3 = 2/3.",
  },
  {
    id: 20,
    type: "numeric",
    question: "Working alone at its constant rate, Pump A can fill a tank in 6 hours; Pump B, working alone at its constant rate, can fill the same tank in 4 hours. How many hours will it take the two pumps, working together, to fill the empty tank?",
    answer: 2.4,
    explanation: "Combined rate = 1/6 + 1/4 = 5/12 tank per hour, so the time is 12/5 = 2.4 hours.",
  },
  {
    id: 21,
    question: "In how many different ways can 5 different books be arranged on a shelf if 2 particular books must be placed next to each other?",
    options: ["24", "48", "60", "96", "120"],
    answer: "48",
    explanation: "Treat the 2 books as one unit: 4 units can be arranged in 4! = 24 ways, and the 2 books can be ordered within the unit in 2 ways. 24 × 2 = 48.",
  },
  {
    id: 22,
    question: "In a sequence, a₁ = 3 and aₙ = 2aₙ₋₁ − 1 for all n ≥ 2. What is the value of a₆?",
    options: ["33", "63", "65", "127", "129"],
    answer: "65",
    explanation: "a₂ = 5, a₃ = 9, a₄ = 17, a₅ = 33, a₆ = 65. (In general aₙ = 2ⁿ + 1.)",
  },
  {
    id: 23,
    type: "multi",
    question: "If |2x − 3| ≤ 5, which of the following could be the value of x?",
    options: ["−2", "−1", "0", "2.5", "4", "4.5"],
    answer: ["−1", "0", "2.5", "4"],
    explanation: "−5 ≤ 2x − 3 ≤ 5 gives −2 ≤ 2x ≤ 8, so −1 ≤ x ≤ 4. The endpoints −1 and 4 are included; −2 and 4.5 are outside.",
  },
  {
    id: 24,
    type: "numeric",
    question: "If 1/a + 1/b = 1/2 and a = 3, what is the value of b?",
    answer: 6,
    explanation: "1/b = 1/2 − 1/3 = 1/6, so b = 6.",
  },
  {
    id: 25,
    question: "What is the area of the triangle in the xy-plane with vertices (0, 0), (8, 0) and (5, 6)?",
    options: ["20", "24", "30", "40", "48"],
    answer: "24",
    explanation: "The base along the x-axis is 8 and the height is the y-coordinate of (5, 6), which is 6. Area = ½ × 8 × 6 = 24.",
  },
  {
    id: 26,
    question: "An investment of $2,000 earns 5 percent annual interest, compounded annually. What is the value of the investment at the end of 3 years?",
    options: ["$2,300.00", "$2,315.25", "$2,331.00", "$2,400.00", "$2,500.00"],
    answer: "$2,315.25",
    explanation: "2,000 × 1.05³ = 2,000 × 1.157625 = $2,315.25. ($2,300 would be simple interest.)",
  },
  {
    id: 27,
    type: "numeric",
    question: "The ratio of boys to girls in a class is 3 to 5. If 6 more boys join the class, the ratio of boys to girls becomes 1 to 1. How many students were originally in the class?",
    answer: 24,
    explanation: "Let there be 3k boys and 5k girls. Then 3k + 6 = 5k, so k = 3: originally 9 boys and 15 girls, 24 students.",
  },
];
